import { apiFetch } from '@/lib/api';

export type WorkspaceKind = 'case' | 'search';

export interface WorkspaceItem {
  id: string;
  kind: WorkspaceKind;
  agency: string;
  /** `process_number_canonical` for a case; the screen's query string for a search. */
  key: string;
  label: string;
  note: string;
  created_at: string;
  updated_at: string;
  snapshot_at: string | null;
  /** The client folder it is shared in; null keeps it private. */
  folder_id?: string | null;
  folder_name?: string | null;
}

/** Another member's item, shared in one of the firm's folders: read-only here. */
export interface SharedWorkspaceItem extends WorkspaceItem {
  owner_name: string;
}

export interface WorkspaceList {
  items: WorkspaceItem[];
  /** Items other members of the firm shared in its folders, within this account's licence. */
  shared?: SharedWorkspaceItem[];
  firm?: string | null;
}

export interface ClientFolder {
  id: string;
  name: string;
  items: number;
  created_by: string | null;
}

export interface ClientFolders {
  /** null for an account of no firm, which has no folders. */
  firm: string | null;
  folders: ClientFolder[];
}

export function fetchFolders(): Promise<ClientFolders> {
  return apiFetch<ClientFolders>('/api/v1/workspace/folders');
}

export function createFolder(name: string): Promise<ClientFolder> {
  return apiFetch<ClientFolder>('/api/v1/workspace/folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function renameFolder(id: string, name: string): Promise<ClientFolder> {
  return apiFetch<ClientFolder>(`/api/v1/workspace/folders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deleteFolder(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/v1/workspace/folders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** Share one of the caller's items in a folder of their firm, or (null) make it private. */
export function moveWorkspaceItem(id: string, folderId: string | null): Promise<WorkspaceItem> {
  return apiFetch<WorkspaceItem>(`/api/v1/workspace/${encodeURIComponent(id)}/folder`, {
    method: 'PUT',
    body: JSON.stringify({ folder_id: folderId }),
  });
}

export interface BulletinDraft {
  /** false for an account of no firm: the draft then stays in this browser. */
  shared: boolean;
  firm: string | null;
  comment: string;
  signature: string;
  updated_by: string | null;
  updated_at: string | null;
}

export function fetchBulletinDraft(agency: string): Promise<BulletinDraft> {
  return apiFetch<BulletinDraft>(
    `/api/v1/workspace/bulletin-draft?agency=${encodeURIComponent(agency)}`,
  );
}

export function saveBulletinDraft(
  agency: string,
  draft: { comment: string; signature: string },
): Promise<BulletinDraft> {
  return apiFetch<BulletinDraft>('/api/v1/workspace/bulletin-draft', {
    method: 'PUT',
    body: JSON.stringify({ agency, ...draft }),
  });
}

export interface WorkspaceItemIn {
  kind: WorkspaceKind;
  agency: string;
  key: string;
  label?: string;
  note?: string;
}

/** The caller's own items, newest edit first. Never anyone else's. */
export function fetchWorkspace(
  filter: { agency?: string; kind?: WorkspaceKind; key?: string } = {},
): Promise<WorkspaceList> {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(filter)) {
    if (value) params.set(name, value);
  }
  const query = params.toString();
  return apiFetch<WorkspaceList>(`/api/v1/workspace${query ? `?${query}` : ''}`);
}

/** Insert, or update the label and note of the row the natural key names. */
export function saveWorkspaceItem(item: WorkspaceItemIn): Promise<WorkspaceItem> {
  return apiFetch<WorkspaceItem>('/api/v1/workspace', {
    method: 'PUT',
    body: JSON.stringify(item),
  });
}

export async function deleteWorkspaceItem(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/v1/workspace/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type WatchStatus = 'changed' | 'unchanged' | 'unavailable';

export interface WatchChange {
  field: string;
  before: unknown;
  after: unknown;
}

/** A marked case found on one of the newest pautas the archive holds. */
export interface WatchPauta {
  session_number: number | null;
  session_kind: string | null;
  session_date: string;
  /** Where the ARCHIVE stands on the item -- never a forecast. */
  standing: 'decided_here' | 'decided_elsewhere' | 'not_in_archive';
}

export interface WorkspaceWatch {
  status: WatchStatus;
  changes: WatchChange[];
  pauta?: WatchPauta | null;
}

/**
 * What moved in the corpus since each item was saved or last marked seen, keyed by
 * item id. `unavailable` means no baseline or an unreadable corpus -- never "no
 * change".
 */
export function fetchWorkspaceChanges(): Promise<{ watch: Record<string, WorkspaceWatch> }> {
  return apiFetch<{ watch: Record<string, WorkspaceWatch> }>('/api/v1/workspace/changes');
}

/** Take the corpus as it is now as the item's new baseline. */
export function markWorkspaceSeen(id: string): Promise<WorkspaceItem> {
  return apiFetch<WorkspaceItem>(`/api/v1/workspace/${encodeURIComponent(id)}/seen`, {
    method: 'POST',
  });
}

/** One alert of the inbox. Computed on the server when asked; never sent. */
export interface WorkspaceAlert {
  /** Natural key: the case and the session date, or the item and the exact change. */
  key: string;
  kind: 'pauta' | 'ac_published' | 'case_changed' | 'tcc_signed' | 'search_changed';
  /** The workspace item behind the alert; null for a sector alert. */
  item_id: string | null;
  item_key: string;
  label: string;
  /** The session date of a pauta alert. */
  when: string | null;
  upcoming: boolean;
  session_number: number | null;
  session_kind: string | null;
  /** Where the ARCHIVE stands on the item -- never a forecast. */
  standing: 'decided_here' | 'decided_elsewhere' | 'not_in_archive' | null;
  changes: WatchChange[];
  read: boolean;
  /** Sector alerts only. */
  nup?: string | null;
  sections?: string[];
  /** The document's SEI address. */
  url?: string | null;
  edital_number?: string | null;
  nature?: string | null;
}

export interface WorkspaceAlerts {
  alerts: WorkspaceAlert[];
  unread: number;
  /** The CNAE sections followed, and the newest SEI ledger document: how fresh sector alerts are. */
  sectors_followed?: string[];
  sector_as_of?: string | null;
  /** Items that could not be looked at: not an alert and not reassurance. */
  unavailable: number;
}

export function fetchWorkspaceAlerts(): Promise<WorkspaceAlerts> {
  return apiFetch<WorkspaceAlerts>('/api/v1/workspace/alerts');
}

export async function markAlertsRead(keys: string[]): Promise<void> {
  await apiFetch<unknown>('/api/v1/workspace/alerts/read', {
    method: 'POST',
    body: JSON.stringify({ keys }),
  });
}

export interface CalendarFeed {
  enabled: boolean;
  created_at: string | null;
  last_read_at: string | null;
  /** Present ONLY in the response that created the feed: only a hash is stored. */
  path: string | null;
}

export function fetchCalendarFeed(): Promise<CalendarFeed> {
  return apiFetch<CalendarFeed>('/api/v1/workspace/calendar');
}

/** Create the feed, or replace it: the previous URL stops working at once. */
export function createCalendarFeed(): Promise<CalendarFeed> {
  return apiFetch<CalendarFeed>('/api/v1/workspace/calendar', { method: 'POST' });
}

export async function revokeCalendarFeed(): Promise<void> {
  await apiFetch<unknown>('/api/v1/workspace/calendar', { method: 'DELETE' });
}

export interface FollowSectors {
  /** False when the sector data is not loaded here. */
  available: boolean;
  options: { code: string; label: string }[];
  following: string[];
}

/** The CNAE sections this account may follow for CADE sector alerts, and those it follows. */
export function fetchSectors(): Promise<FollowSectors> {
  return apiFetch<FollowSectors>('/api/v1/workspace/sectors');
}

/** Replace the followed sections with exactly these. */
export function saveSectors(codes: string[]): Promise<FollowSectors> {
  return apiFetch<FollowSectors>('/api/v1/workspace/sectors', {
    method: 'PUT',
    body: JSON.stringify({ codes }),
  });
}

